import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Box, Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Joi from '@hapi/joi';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import CustomTextField from 'components/Forms/TextField';
import CustomSelect from 'components/Forms/CustomSelect';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import { isEditSaveDisabled, resolveApiErrorMessage } from 'utils';

const TRAINING_MODULE_STATUSES = ['active', 'archived'];

const JOI_ERROR_MESSAGES = {
  'any.required': () => i18next.t('errors.fieldIsRequired'),
  'string.empty': () => i18next.t('errors.fieldIsRequired'),
  'object.base': () => i18next.t('errors.fieldIsRequired'),
  'string.max': detail => i18next.t('errors.maxLength', { max: detail.context?.limit }),
};

const useStyles = makeStyles(() => ({
  actions: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
}));

const validate = values => {
  const schema = Joi.object({
    code: Joi.string().trim().min(1).max(64).required(),
    name: Joi.string().trim().min(1).max(255).required(),
    description: Joi.string().allow('').optional(),
    status: Joi.object({
      value: Joi.string()
        .valid(...TRAINING_MODULE_STATUSES)
        .required(),
      label: Joi.string().required(),
    }).required(),
  });

  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) {
    return {};
  }

  return error.details.reduce((errors, detail) => {
    const field = detail.path[0] === 'status' ? 'status' : detail.path[0];
    const resolveMessage = JOI_ERROR_MESSAGES[detail.type];
    errors[field] = resolveMessage ? resolveMessage(detail) : detail.type;
    return errors;
  }, {});
};

const TrainingModuleForm = ({
  mode,
  layout,
  initialValues,
  onSubmitForm,
  onCancel,
  backUrl,
  submitting,
  readOnly = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation('common');
  const isPageLayout = layout === 'page';

  const statusOptions = TRAINING_MODULE_STATUSES.map(value => ({
    value,
    label: t(`trainingModules.statuses.${value}`),
  }));

  const defaultStatus = statusOptions.find(option => option.value === 'active') || statusOptions[0];

  const formInitialValues = {
    code: initialValues?.code || '',
    name: initialValues?.name || '',
    description: initialValues?.description || '',
    status: statusOptions.find(option => option.value === initialValues?.status) || defaultStatus,
  };

  const handleSubmit = async values => {
    const payload = {
      code: values.code.trim(),
      name: values.name.trim(),
      description: values.description?.trim() || '',
      status: values.status?.value,
    };

    const result = await onSubmitForm(payload);
    if (result?.error) {
      return {
        [FORM_ERROR]: resolveApiErrorMessage(result.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      };
    }
    return undefined;
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (backUrl) {
      history.push(backUrl);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={formInitialValues}
      validate={validate}
      render={({ handleSubmit: submitForm, submitting: formSubmitting, pristine, submitError, submitFailed }) => {
        const isBusy = submitting || formSubmitting;
        const isSaveDisabled = isEditSaveDisabled({
          submitting: isBusy,
          pristine,
          isEditMode: mode === 'edit',
        });

        return (
          <form onSubmit={readOnly ? event => event.preventDefault() : submitForm}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="code"
                  id="code"
                  label={t('trainingModules.code')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || readOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="status"
                  id="status"
                  label={t('tablesLabels.status')}
                  component={CustomSelect}
                  options={statusOptions}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || readOnly}
                  isDisabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  type="text"
                  name="name"
                  id="name"
                  label={t('tablesLabels.title')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  type="text"
                  name="description"
                  id="description"
                  label={t('tablesLabels.description')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  disabled={isBusy || readOnly}
                />
              </Grid>
            </Grid>

            {submitFailed && submitError && <SubmitErrorMessage message={submitError} />}

            <Box className={classes.actions}>
              <Button variant="outlined" onClick={handleCancel} disabled={isBusy}>
                {isPageLayout ? t('buttons.back') : t('buttons.cancel')}
              </Button>
              {!readOnly && (
                <Button type="submit" color="primary" variant="contained" disabled={isSaveDisabled}>
                  {mode === 'edit' ? t('buttons.save') : t('trainingModules.actionAdd')}
                </Button>
              )}
            </Box>
          </form>
        );
      }}
    />
  );
};

TrainingModuleForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  layout: PropTypes.oneOf(['modal', 'page']),
  initialValues: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
  }),
  onSubmitForm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  backUrl: PropTypes.string,
  submitting: PropTypes.bool,
  readOnly: PropTypes.bool,
};

TrainingModuleForm.defaultProps = {
  mode: 'create',
  layout: 'modal',
  initialValues: null,
  onCancel: null,
  backUrl: null,
  submitting: false,
  readOnly: false,
};

export default TrainingModuleForm;
